/**
 * HoopsPasskeyKit — WebAuthn passkey integration for Hoops Account contracts.
 *
 * Unlike smart-account-kit which deploys the OZ smart-account (different ABI),
 * this kit deploys our Hoops Account contract with native secp256r1 __check_auth.
 *
 * Flow:
 *  1. Register a WebAuthn passkey (secp256r1)
 *  2. Deploy our Account contract on Soroban
 *  3. Call initialize_with_passkey(owner=contract, router, pubkey)
 *  4. Sign operations via __check_auth using passkey biometrics
 */

import { startRegistration, startAuthentication } from "@simplewebauthn/browser";
import type { AuthenticatorTransportFuture } from "@simplewebauthn/browser";
import {
  Account,
  Keypair,
  TransactionBuilder,
  rpc,
  Operation,
  Address,
  xdr,
  hash,
  BASE_FEE,
  StrKey,
  Contract,
  scValToNative,
} from "@stellar/stellar-sdk";

// ============================================================================
// Types
// ============================================================================

export interface HoopsPasskeyConfig {
  rpcUrl: string;
  networkPassphrase: string;
  /** Our Hoops account WASM hash (with passkey __check_auth) */
  wasmHash: string;
  /** Hoops Router contract address */
  routerAddress: string;
  /** WebAuthn relying party ID (defaults to window.location.hostname) */
  rpId?: string;
  /** WebAuthn relying party name */
  rpName?: string;
}

export interface CreateWalletResult {
  contractId: string;
  credentialId: string;
  deployHash: string;
  initHash: string;
}

/** Quote returned by Router::get_all_quotes (scValToNative output) */
export interface SwapQuote {
  adapter_id: bigint | number;
  pool_address: string;
  token_in: string;
  token_out: string;
  amount_in: bigint | number;
  amount_out: bigint | number;
  pool_type: number;
  lp_token: string;
}

export interface FundResult {
  success: boolean;
  hash: string;
  amount?: number;
  error?: string;
}

export interface TransactionResult {
  success: boolean;
  hash: string;
  error?: string;
}

// ============================================================================
// Constants
// ============================================================================

const FRIENDBOT_URL = "https://friendbot.stellar.org";
// Storage keys are scoped by credentialId to prevent cross-user leakage.
// Legacy global keys (hoops_pk_*) are migrated on first access then deleted.
const STORAGE_PREFIX = "hoops_pk_";
function scopedKey(credentialId: string, suffix: string): string {
  return `${STORAGE_PREFIX}${credentialId}:${suffix}`;
}
// Legacy (pre-scoping) global key names — used only for migration
const LEGACY_KEYS = {
  contract: "hoops_pk_contractId",
  credential: "hoops_pk_credentialId",
  pubkey: "hoops_pk_publicKey",
  deployer: "hoops_pk_deployer",
  transports: "hoops_pk_transports",
} as const;
const WEBAUTHN_TIMEOUT_MS = 300_000;
const POLL_ATTEMPTS = 30;

// ============================================================================
// LpPlan type (matches contract's LpPlan struct)
// ============================================================================

export interface LpPlan {
  tokenA: string;
  tokenB: string;
  amountA: bigint;
  amountB: bigint;
  adapterId: bigint;
  poolAddress: string;
}

export interface RedeemPlan {
  lpToken: string;
  lpAmount: bigint;
  adapterId: bigint;
  poolAddress: string;
}

// ============================================================================
// ScVal Helpers
// ============================================================================

/** Build an i128 ScVal from a bigint (avoids repeating the lo/hi split). */
function i128ScVal(value: bigint): xdr.ScVal {
  return xdr.ScVal.scvI128(new xdr.Int128Parts({
    lo: xdr.Uint64.fromString((value & 0xFFFFFFFFFFFFFFFFn).toString()),
    hi: xdr.Int64.fromString((value >> 64n).toString()),
  }));
}

/** Build a u128 ScVal from a bigint (for Aqua pool amounts). */
function u128ScVal(value: bigint): xdr.ScVal {
  return xdr.ScVal.scvU128(new xdr.UInt128Parts({
    lo: xdr.Uint64.fromString((value & 0xFFFFFFFFFFFFFFFFn).toString()),
    hi: xdr.Uint64.fromString((value >> 64n).toString()),
  }));
}

/** Parse an i128 ScVal back to bigint. */
function scValToI128(val: xdr.ScVal): bigint {
  try {
    const i128 = val.i128();
    const hi = BigInt(i128.hi().toString());
    const lo = BigInt(i128.lo().toString());
    return (hi << 64n) | lo;
  } catch {
    return 0n;
  }
}

/** Build an Address ScVal from a Stellar address string. */
function addrScVal(address: string): xdr.ScVal {
  return xdr.ScVal.scvAddress(Address.fromString(address).toScAddress());
}

/** Check if a sendTransaction error is retryable (stale sequence or account propagation delay). */
function isRetryableSendError(errorResult: xdr.TransactionResult | undefined): boolean {
  try {
    if (errorResult) {
      const code = errorResult.result().switch().value;
      // txBAD_SEQ (-5): stale sequence number after recent TX
      // txNO_ACCOUNT (-8): account not yet visible after Friendbot funding
      return code === -5 || code === -8;
    }
  } catch { /* parse failure */ }
  return false;
}

/** Check if a simulation error is a transient ledger propagation issue. */
function isRetryableSimError(errorMsg: string): boolean {
  // These are specific errors caused by the RPC simulation engine lagging
  // behind the actual ledger state after a recent TX:
  // 1. "account entry is missing" — XLM SAC can't find a Friendbot-funded account
  // 2. "non-existing value for contract instance" — contract just deployed but
  //    simulation engine hasn't indexed it yet (deploy TX confirmed, init runs immediately)
  return errorMsg.includes("account entry is missing")
    || errorMsg.includes("non-existing value for contract instance");
}

const TX_RETRIES = 3;
const TX_RETRY_DELAY_MS = 3000;

/** Build an alphabetically-sorted ScvMap matching the contract's LpPlan struct. */
function buildLpPlanScVal(plan: LpPlan): xdr.ScVal {
  return xdr.ScVal.scvMap([
    new xdr.ScMapEntry({
      key: xdr.ScVal.scvSymbol("adapter_id"),
      val: i128ScVal(plan.adapterId),
    }),
    new xdr.ScMapEntry({
      key: xdr.ScVal.scvSymbol("amount_a"),
      val: i128ScVal(plan.amountA),
    }),
    new xdr.ScMapEntry({
      key: xdr.ScVal.scvSymbol("amount_b"),
      val: i128ScVal(plan.amountB),
    }),
    new xdr.ScMapEntry({
      key: xdr.ScVal.scvSymbol("pool_address"),
      val: addrScVal(plan.poolAddress),
    }),
    new xdr.ScMapEntry({
      key: xdr.ScVal.scvSymbol("token_a"),
      val: addrScVal(plan.tokenA),
    }),
    new xdr.ScMapEntry({
      key: xdr.ScVal.scvSymbol("token_b"),
      val: addrScVal(plan.tokenB),
    }),
  ]);
}

/** Build an alphabetically-sorted ScvMap matching the contract's RedeemPlan struct. */
function buildRedeemPlanScVal(plan: RedeemPlan): xdr.ScVal {
  return xdr.ScVal.scvMap([
    new xdr.ScMapEntry({
      key: xdr.ScVal.scvSymbol("adapter_id"),
      val: i128ScVal(plan.adapterId),
    }),
    new xdr.ScMapEntry({
      key: xdr.ScVal.scvSymbol("lp_amount"),
      val: i128ScVal(plan.lpAmount),
    }),
    new xdr.ScMapEntry({
      key: xdr.ScVal.scvSymbol("lp_token"),
      val: addrScVal(plan.lpToken),
    }),
    new xdr.ScMapEntry({
      key: xdr.ScVal.scvSymbol("pool_address"),
      val: addrScVal(plan.poolAddress),
    }),
  ]);
}

// ============================================================================
// Base64url Helpers
// ============================================================================

function b64urlEncode(data: Buffer | Uint8Array): string {
  return Buffer.from(data)
    .toString("base64")
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");
}

function b64urlDecode(str: string): Buffer {
  let b64 = str.replace(/-/g, "+").replace(/_/g, "/");
  while (b64.length % 4) b64 += "=";
  return Buffer.from(b64, "base64");
}

// ============================================================================
// Crypto Helpers
// ============================================================================

/** Extract uncompressed P-256 public key (65 bytes) from WebAuthn attestation. */
function extractPublicKeyFromAttestation(response: {
  publicKey?: string;
  authenticatorData?: string;
  attestationObject?: string;
}): Uint8Array {
  if (response.publicKey) {
    const raw = b64urlDecode(response.publicKey);
    const pk = raw.subarray(raw.length - 65);
    if (pk.length === 65 && pk[0] === 0x04) return new Uint8Array(pk);
  }

  if (response.authenticatorData) {
    const ad = b64urlDecode(response.authenticatorData);
    const credIdLen = (ad[53] << 8) | ad[54];
    const x = ad.subarray(65 + credIdLen, 97 + credIdLen);
    const y = ad.subarray(100 + credIdLen, 132 + credIdLen);
    return new Uint8Array([0x04, ...x, ...y]);
  }

  if (response.attestationObject) {
    const att = b64urlDecode(response.attestationObject);
    const prefix = Buffer.from([0xa5, 0x01, 0x02, 0x03, 0x26, 0x20, 0x01, 0x21, 0x58, 0x20]);
    const idx = att.indexOf(prefix) + prefix.length;
    const x = att.subarray(idx, idx + 32);
    const y = att.subarray(idx + 35, idx + 67);
    return new Uint8Array([0x04, ...x, ...y]);
  }

  throw new Error("Could not extract public key from attestation response");
}

/** Convert DER-encoded ECDSA signature to 64-byte compact (r||s) with low-S. */
function compactSignature(der: Uint8Array): Uint8Array {
  let offset = 2;
  const rLen = der[offset + 1];
  const r = der.subarray(offset + 2, offset + 2 + rLen);
  offset += 2 + rLen;
  const sLen = der[offset + 1];
  const s = der.subarray(offset + 2, offset + 2 + sLen);

  const rBig = BigInt("0x" + Buffer.from(r).toString("hex"));
  let sBig = BigInt("0x" + Buffer.from(s).toString("hex"));

  const n = BigInt("0xffffffff00000000ffffffffffffffffbce6faada7179e84f3b9cac2fc632551");
  if (sBig > n / 2n) sBig = n - sBig;

  const rPad = Buffer.from(rBig.toString(16).padStart(64, "0"), "hex");
  const sPad = Buffer.from(sBig.toString(16).padStart(64, "0"), "hex");
  return new Uint8Array([...rPad, ...sPad]);
}

/** Derive contract address deterministically from deployer + salt. */
function deriveContractId(
  deployerPubkey: string,
  salt: Buffer,
  networkPassphrase: string,
): string {
  const preimage = xdr.HashIdPreimage.envelopeTypeContractId(
    new xdr.HashIdPreimageContractId({
      networkId: hash(Buffer.from(networkPassphrase)),
      contractIdPreimage: xdr.ContractIdPreimage.contractIdPreimageFromAddress(
        new xdr.ContractIdPreimageFromAddress({
          address: Address.fromString(deployerPubkey).toScAddress(),
          salt,
        }),
      ),
    }),
  );
  return StrKey.encodeContract(hash(preimage.toXDR()));
}

/**
 * Simulate, prepare, sign, send a transaction with retry for transient errors:
 * - Simulation: "account entry is missing" (ledger propagation delay after Friendbot)
 * - Send: txBAD_SEQ / txNO_ACCOUNT (stale sequence or account not yet visible)
 * Retries rebuild the TX with a fresh account sequence each time.
 */
async function simulateSignSend(
  server: rpc.Server,
  tx: Transaction,
  signer: Keypair,
  networkPassphrase: string,
): Promise<string> {
  const opXdr = tx.toEnvelope().v1().tx().operations()[0];
  const fee = tx.fee;

  for (let attempt = 0; ; attempt++) {
    // On retry, rebuild the transaction with a fresh account sequence
    let currentTx = tx;
    if (attempt > 0) {
      const freshAccount = await server.getAccount(signer.publicKey());
      currentTx = new TransactionBuilder(freshAccount, { fee, networkPassphrase })
        .addOperation(opXdr)
        .setTimeout(60)
        .build();
    }

    const simResult = await server.simulateTransaction(currentTx);
    if (rpc.Api.isSimulationError(simResult)) {
      if (attempt < TX_RETRIES && isRetryableSimError(simResult.error)) {
        await new Promise(r => setTimeout(r, TX_RETRY_DELAY_MS));
        continue;
      }
      throw new Error(`Simulation failed: ${simResult.error}`);
    }

    const prepared = rpc.assembleTransaction(
      currentTx,
      simResult as rpc.Api.SimulateTransactionSuccessResponse,
    ).build();
    prepared.sign(signer);

    const sendResult = await server.sendTransaction(prepared);
    if (sendResult.status === ("ERROR" as rpc.Api.SendTransactionStatus)) {
      if (attempt < TX_RETRIES && isRetryableSendError(sendResult.errorResult)) {
        await new Promise(r => setTimeout(r, TX_RETRY_DELAY_MS));
        continue;
      }
      throw new Error(`Send failed: ${sendResult.errorResult?.toXDR("base64") ?? "unknown"}`);
    }

    const result = await server.pollTransaction(sendResult.hash, { attempts: POLL_ATTEMPTS });
    if (result.status !== rpc.Api.GetTransactionStatus.SUCCESS) {
      throw new Error(`Transaction failed on-chain (status: ${String(result.status)})`);
    }

    return sendResult.hash;
  }
}

// Type alias for the Transaction type returned by TransactionBuilder.build()
type Transaction = ReturnType<typeof TransactionBuilder.prototype.build>;

// ============================================================================
// Main Kit
// ============================================================================

export class HoopsPasskeyKit {
  private rpcUrl: string;
  private networkPassphrase: string;
  private wasmHash: Buffer;
  private routerAddress: string;
  private server: rpc.Server;
  private rpId: string | undefined;
  private rpName: string;

  private _contractId: string | null = null;
  private _credentialId: string | null = null;
  private _transports: AuthenticatorTransportFuture[] = [];
  private _publicKey: Uint8Array | null = null;
  private _deployer: Keypair | null = null;
  private _sessionKeypair: Keypair | null = null;

  constructor(config: HoopsPasskeyConfig) {
    this.rpcUrl = config.rpcUrl;
    this.networkPassphrase = config.networkPassphrase;
    this.wasmHash = Buffer.from(config.wasmHash, "hex");
    this.routerAddress = config.routerAddress;
    this.rpId = config.rpId;
    this.rpName = config.rpName || "Hoops Finance";
    this.server = new rpc.Server(this.rpcUrl);
  }

  get contractId() { return this._contractId; }
  get credentialId() { return this._credentialId; }
  get deployerPublicKey() { return this._deployer?.publicKey() ?? null; }
  get isConnected() { return !!this._contractId; }

  /** Set contract ID for read-only queries (cross-device fallback).
   *  Does NOT enable signing — deployer and credential are still needed for that. */
  setContractIdReadOnly(contractId: string): void {
    this._contractId = contractId;
  }

  // --------------------------------------------------------------------------
  // Cross-device provisioning — attach to an existing wallet on a new device
  // --------------------------------------------------------------------------

  /**
   * Provision a new deployer (fee-payer) for an existing wallet on this device.
   * The wallet was deployed on another device; we just need a funded account
   * to pay transaction fees. The passkey handles actual authorization.
   *
   * On testnet, the deployer is funded via Friendbot.
   * On mainnet, the OZ Relayer will replace this entirely.
   */
  async provisionForExistingWallet(
    contractId: string,
    credentialId: string,
    p256PublicKeyHex?: string,
    transports?: AuthenticatorTransportFuture[],
  ): Promise<void> {
    // Verify the contract exists on-chain
    await this.server.getContractData(contractId, xdr.ScVal.scvLedgerKeyContractInstance());

    // Generate and fund a fresh deployer for fee payment
    const deployer = Keypair.random();
    const fbRes = await fetch(`${FRIENDBOT_URL}?addr=${deployer.publicKey()}`);
    if (!fbRes.ok) throw new Error(`Friendbot failed: ${await fbRes.text()}`);

    // Set full kit state
    this._contractId = contractId;
    this._credentialId = credentialId;
    this._deployer = deployer;
    this._transports = transports ?? [];
    if (p256PublicKeyHex) {
      this._publicKey = new Uint8Array(
        (p256PublicKeyHex.match(/.{1,2}/g) ?? []).map(byte => parseInt(byte, 16)),
      );
    }

    // Persist to scoped localStorage so it survives page reloads
    this.saveToStorage();
  }

  // --------------------------------------------------------------------------
  // Retry wrapper for getAccount — handles RPC propagation delays after
  // Friendbot funding or recent transactions.
  // --------------------------------------------------------------------------
  private async getAccountWithRetry(publicKey: string, maxAttempts = 10) {
    for (let i = 0; i < maxAttempts; i++) {
      try {
        return await this.server.getAccount(publicKey);
      } catch {
        if (i === maxAttempts - 1) throw new Error(`Account not found after ${String(maxAttempts)} attempts: ${publicKey}`);
        await new Promise(r => setTimeout(r, 3000));
      }
    }
    throw new Error(`Account not found: ${publicKey}`);
  }

  // --------------------------------------------------------------------------
  // Create Wallet — register passkey, deploy contract, initialize
  // --------------------------------------------------------------------------
  async createWallet(appName: string, userName: string): Promise<CreateWalletResult> {
    // 1. Register WebAuthn passkey
    const now = new Date();
    const displayName = `${userName} — ${now.toLocaleString()}`;

    const regResponse = await startRegistration({
      optionsJSON: {
        challenge: b64urlEncode(crypto.getRandomValues(new Uint8Array(32))),
        rp: { id: this.rpId || window.location.hostname, name: appName || this.rpName },
        user: {
          id: b64urlEncode(Buffer.from(`${userName}:${String(now.getTime())}:${String(Math.random())}`)),
          name: displayName,
          displayName,
        },
        authenticatorSelection: {
          residentKey: "preferred",
          userVerification: "preferred",
        },
        pubKeyCredParams: [{ alg: -7, type: "public-key" }],
        timeout: WEBAUTHN_TIMEOUT_MS,
      },
    });

    const credentialId = regResponse.id;
    const publicKey = extractPublicKeyFromAttestation(regResponse.response);
    const transports = regResponse.response.transports ?? [];

    // 2. Create deployer keypair and fund via Friendbot
    const deployer = Keypair.random();
    const fbRes = await fetch(`${FRIENDBOT_URL}?addr=${deployer.publicKey()}`);
    if (!fbRes.ok) throw new Error(`Friendbot failed: ${await fbRes.text()}`);

    // 3. Compute deterministic contract address
    const salt = hash(b64urlDecode(credentialId));
    const contractId = deriveContractId(deployer.publicKey(), salt, this.networkPassphrase);

    // 4. Deploy contract instance
    const deployHash = await this.deployContract(deployer, salt);

    // 5. Initialize with passkey
    const initHash = await this.initializeContract(deployer, contractId, publicKey);

    // 6. Persist state
    this._contractId = contractId;
    this._credentialId = credentialId;
    this._publicKey = publicKey;
    this._deployer = deployer;
    this._transports = transports;
    this.saveToStorage();

    return { contractId, credentialId, deployHash, initHash };
  }

  // --------------------------------------------------------------------------
  // Deploy using existing passkey — reuses the P256 key from auth signup
  // No second WebAuthn registration needed.
  // --------------------------------------------------------------------------
  async deployWithExistingPasskey(
    credentialId: string,
    p256PublicKeyHex: string,
    transports?: AuthenticatorTransportFuture[],
  ): Promise<CreateWalletResult> {
    // Convert hex P256 key (130 chars, "04..." uncompressed) to Uint8Array
    const publicKey = new Uint8Array(
      (p256PublicKeyHex.match(/.{1,2}/g) ?? []).map(byte => parseInt(byte, 16)),
    );

    if (publicKey.length !== 65 || publicKey[0] !== 0x04) {
      throw new Error(`Invalid P256 public key: expected 65 bytes starting with 04, got ${String(publicKey.length)} bytes`);
    }

    // 1. Create deployer keypair and fund via Friendbot
    const deployer = Keypair.random();
    const fbRes = await fetch(`${FRIENDBOT_URL}?addr=${deployer.publicKey()}`);
    if (!fbRes.ok) throw new Error(`Friendbot failed: ${await fbRes.text()}`);

    // 2. Compute deterministic contract address (same salt derivation as createWallet)
    const salt = hash(b64urlDecode(credentialId));
    const contractId = deriveContractId(deployer.publicKey(), salt, this.networkPassphrase);

    // 3. Deploy contract instance
    const deployHash = await this.deployContract(deployer, salt);

    // 4. Initialize with the existing passkey's P256 public key
    const initHash = await this.initializeContract(deployer, contractId, publicKey);

    // 5. Persist state
    this._contractId = contractId;
    this._credentialId = credentialId;
    this._publicKey = publicKey;
    this._deployer = deployer;
    this._transports = transports ?? [];
    this.saveToStorage();

    return { contractId, credentialId, deployHash, initHash };
  }

  // --------------------------------------------------------------------------
  // Deploy contract (createContract operation)
  // --------------------------------------------------------------------------
  private async deployContract(deployer: Keypair, salt: Buffer): Promise<string> {
    const account = await this.getAccountWithRetry(deployer.publicKey());

    const deployOp = Operation.invokeHostFunction({
      func: xdr.HostFunction.hostFunctionTypeCreateContract(
        new xdr.CreateContractArgs({
          contractIdPreimage: xdr.ContractIdPreimage.contractIdPreimageFromAddress(
            new xdr.ContractIdPreimageFromAddress({
              address: Address.fromString(deployer.publicKey()).toScAddress(),
              salt,
            }),
          ),
          executable: xdr.ContractExecutable.contractExecutableWasm(this.wasmHash),
        }),
      ),
      auth: [],
    });

    const tx = new TransactionBuilder(account, {
      fee: BASE_FEE,
      networkPassphrase: this.networkPassphrase,
    })
      .addOperation(deployOp)
      .setTimeout(60)
      .build();

    return simulateSignSend(this.server, tx, deployer, this.networkPassphrase);
  }

  // --------------------------------------------------------------------------
  // Initialize contract with passkey
  // --------------------------------------------------------------------------
  private async initializeContract(
    deployer: Keypair,
    contractId: string,
    publicKey: Uint8Array,
  ): Promise<string> {
    const account = await this.getAccountWithRetry(deployer.publicKey());
    const contractAddr = Address.fromString(contractId);
    const routerAddr = Address.fromString(this.routerAddress);

    const initOp = Operation.invokeHostFunction({
      func: xdr.HostFunction.hostFunctionTypeInvokeContract(
        new xdr.InvokeContractArgs({
          contractAddress: contractAddr.toScAddress(),
          functionName: "initialize_with_passkey",
          args: [
            // owner = contract address itself (all auth routes through __check_auth)
            xdr.ScVal.scvAddress(contractAddr.toScAddress()),
            // router
            xdr.ScVal.scvAddress(routerAddr.toScAddress()),
            // passkey_pubkey: BytesN<65>
            xdr.ScVal.scvBytes(Buffer.from(publicKey)),
          ],
        }),
      ),
      auth: [],
    });

    const tx = new TransactionBuilder(account, {
      fee: BASE_FEE,
      networkPassphrase: this.networkPassphrase,
    })
      .addOperation(initOp)
      .setTimeout(60)
      .build();

    return simulateSignSend(this.server, tx, deployer, this.networkPassphrase);
  }

  // --------------------------------------------------------------------------
  // Reconnect from localStorage (session-validated)
  // --------------------------------------------------------------------------

  /**
   * Attempt to restore wallet state from localStorage.
   *
   * @param expectedCredentialId  The credentialId from the current authenticated
   *   session.  When provided the method ONLY loads data whose stored
   *   credentialId matches — preventing cross-user wallet leakage.
   *   Legacy (pre-scoping) global keys are migrated if they match, or
   *   deleted if they don't.
   */
  async connectWallet(
    expectedCredentialId?: string,
  ): Promise<{ contractId: string; credentialId: string } | null> {
    if (typeof window === "undefined") return null;

    let contractId: string | null = null;
    let credentialId: string | null = null;
    let pkHex: string | null = null;
    let deployerSecret: string | null = null;
    let transportsJson: string | null = null;

    if (expectedCredentialId) {
      // --- Try scoped keys first ---
      contractId = localStorage.getItem(scopedKey(expectedCredentialId, "contractId"));
      credentialId = localStorage.getItem(scopedKey(expectedCredentialId, "credentialId"));
      pkHex = localStorage.getItem(scopedKey(expectedCredentialId, "publicKey"));
      deployerSecret = localStorage.getItem(scopedKey(expectedCredentialId, "deployer"));
      transportsJson = localStorage.getItem(scopedKey(expectedCredentialId, "transports"));

      // --- Fallback: migrate legacy global keys if they belong to this user ---
      if (!contractId || !credentialId) {
        const legacyCred = localStorage.getItem(LEGACY_KEYS.credential);
        if (legacyCred && legacyCred === expectedCredentialId) {
          contractId = localStorage.getItem(LEGACY_KEYS.contract);
          credentialId = legacyCred;
          pkHex = localStorage.getItem(LEGACY_KEYS.pubkey);
          deployerSecret = localStorage.getItem(LEGACY_KEYS.deployer);
          transportsJson = localStorage.getItem(LEGACY_KEYS.transports);

          // Migrate to scoped keys and delete legacy
          if (contractId && credentialId && pkHex && deployerSecret) {
            localStorage.setItem(scopedKey(expectedCredentialId, "contractId"), contractId);
            localStorage.setItem(scopedKey(expectedCredentialId, "credentialId"), credentialId);
            localStorage.setItem(scopedKey(expectedCredentialId, "publicKey"), pkHex);
            localStorage.setItem(scopedKey(expectedCredentialId, "deployer"), deployerSecret);
            if (transportsJson) localStorage.setItem(scopedKey(expectedCredentialId, "transports"), transportsJson);
          }
        }
        // Always remove legacy keys (whether they matched or not)
        HoopsPasskeyKit.removeLegacyKeys();
      }
    } else {
      // No session credential — refuse to load anything.
      // Wipe any legacy keys that may be lingering.
      HoopsPasskeyKit.removeLegacyKeys();
      return null;
    }

    if (!contractId || !credentialId || !pkHex || !deployerSecret) return null;

    // Verify contract exists on-chain
    try {
      await this.server.getContractData(contractId, xdr.ScVal.scvLedgerKeyContractInstance());
    } catch {
      return null;
    }

    this._contractId = contractId;
    this._credentialId = credentialId;
    this._publicKey = Buffer.from(pkHex, "hex");
    this._deployer = Keypair.fromSecret(deployerSecret);
    this._transports = transportsJson ? JSON.parse(transportsJson) as AuthenticatorTransportFuture[] : [];

    return { contractId, credentialId };
  }

  // --------------------------------------------------------------------------
  // Fund wallet via Friendbot (transfer XLM to contract)
  // --------------------------------------------------------------------------
  async fundWallet(xlmTokenContract: string): Promise<FundResult> {
    if (!this._contractId) return { success: false, hash: "", error: "Not connected" };

    try {
      // Create a temp account funded by Friendbot
      const temp = Keypair.random();
      const fbRes = await fetch(`${FRIENDBOT_URL}?addr=${temp.publicKey()}`);
      if (!fbRes.ok) return { success: false, hash: "", error: `Friendbot: ${await fbRes.text()}` };

      const account = await this.getAccountWithRetry(temp.publicKey());
      const transferAmount = 9_900n * 10_000_000n; // ~9,900 XLM

      const transferOp = Operation.invokeHostFunction({
        func: xdr.HostFunction.hostFunctionTypeInvokeContract(
          new xdr.InvokeContractArgs({
            contractAddress: Address.fromString(xlmTokenContract).toScAddress(),
            functionName: "transfer",
            args: [
              xdr.ScVal.scvAddress(Address.fromString(temp.publicKey()).toScAddress()),
              xdr.ScVal.scvAddress(Address.fromString(this._contractId).toScAddress()),
              xdr.ScVal.scvI128(new xdr.Int128Parts({
                lo: xdr.Uint64.fromString((transferAmount & 0xFFFFFFFFFFFFFFFFn).toString()),
                hi: xdr.Int64.fromString((transferAmount >> 64n).toString()),
              })),
            ],
          }),
        ),
        auth: [],
      });

      const tx = new TransactionBuilder(account, {
        fee: BASE_FEE,
        networkPassphrase: this.networkPassphrase,
      })
        .addOperation(transferOp)
        .setTimeout(60)
        .build();

      const txHash = await simulateSignSend(this.server, tx, temp, this.networkPassphrase);

      // Poll until the balance is visible in the simulation engine.
      // The TX is confirmed on-chain but simulation state can lag by a few ledgers.
      for (let i = 0; i < 10; i++) {
        await new Promise(r => setTimeout(r, 2000));
        const bal = await this.queryBalance(xlmTokenContract);
        if (bal > 0n) break;
      }

      return { success: true, hash: txHash, amount: 9900 };
    } catch (err) {
      return { success: false, hash: "", error: err instanceof Error ? err.message : "Unknown error" };
    }
  }

  // --------------------------------------------------------------------------
  // Sign a Soroban auth entry with passkey (WebAuthn secp256r1)
  // --------------------------------------------------------------------------
  private async signAuthEntry(
    entry: xdr.SorobanAuthorizationEntry,
    expirationLedger: number,
  ): Promise<xdr.SorobanAuthorizationEntry> {
    if (!this._credentialId) throw new Error("No credential ID");

    const credentials = entry.credentials().address();
    credentials.signatureExpirationLedger(expirationLedger);

    // Compute the signature payload (__check_auth receives this as Hash<32>)
    const preimage = xdr.HashIdPreimage.envelopeTypeSorobanAuthorization(
      new xdr.HashIdPreimageSorobanAuthorization({
        networkId: hash(Buffer.from(this.networkPassphrase)),
        nonce: credentials.nonce(),
        signatureExpirationLedger: credentials.signatureExpirationLedger(),
        invocation: entry.rootInvocation(),
      }),
    );
    const payload = hash(preimage.toXDR());

    // Trigger WebAuthn assertion with challenge = base64url(payload)
    const authResponse = await startAuthentication({
      optionsJSON: {
        challenge: b64urlEncode(payload),
        rpId: this.rpId || window.location.hostname,
        userVerification: "preferred",
        timeout: WEBAUTHN_TIMEOUT_MS,
        allowCredentials: [{
          id: this._credentialId,
          type: "public-key",
          ...(this._transports.length > 0 && { transports: this._transports }),
        }],
      },
    });

    const rawSig = b64urlDecode(authResponse.response.signature);
    const sig64 = compactSignature(rawSig);
    const authenticatorData = b64urlDecode(authResponse.response.authenticatorData);
    const clientDataJSON = b64urlDecode(authResponse.response.clientDataJSON);

    // Encode as AccountSignature::Passkey(Secp256r1Signature) enum.
    // Soroban contracttype enums serialize as ScvVec([ScvSymbol(variant), ...data]).
    const passkeySignature = xdr.ScVal.scvMap([
      new xdr.ScMapEntry({
        key: xdr.ScVal.scvSymbol("authenticator_data"),
        val: xdr.ScVal.scvBytes(authenticatorData),
      }),
      new xdr.ScMapEntry({
        key: xdr.ScVal.scvSymbol("client_data_json"),
        val: xdr.ScVal.scvBytes(clientDataJSON),
      }),
      new xdr.ScMapEntry({
        key: xdr.ScVal.scvSymbol("signature"),
        val: xdr.ScVal.scvBytes(Buffer.from(sig64)),
      }),
    ]);
    credentials.signature(
      xdr.ScVal.scvVec([
        xdr.ScVal.scvSymbol("Passkey"),
        passkeySignature,
      ]),
    );

    return entry;
  }

  /** Sign a Soroban auth entry with the in-memory ed25519 session key. */
  private signAuthEntryWithSession(
    entry: xdr.SorobanAuthorizationEntry,
    expirationLedger: number,
  ): xdr.SorobanAuthorizationEntry {
    if (!this._sessionKeypair) throw new Error("No active session key");

    const credentials = entry.credentials().address();
    credentials.signatureExpirationLedger(expirationLedger);

    const preimage = xdr.HashIdPreimage.envelopeTypeSorobanAuthorization(
      new xdr.HashIdPreimageSorobanAuthorization({
        networkId: hash(Buffer.from(this.networkPassphrase)),
        nonce: credentials.nonce(),
        signatureExpirationLedger: credentials.signatureExpirationLedger(),
        invocation: entry.rootInvocation(),
      }),
    );
    const payload = hash(preimage.toXDR());

    // Ed25519 signature
    const sig = this._sessionKeypair.sign(payload);

    // Encode as AccountSignature::Session(BytesN<64>)
    credentials.signature(
      xdr.ScVal.scvVec([
        xdr.ScVal.scvSymbol("Session"),
        xdr.ScVal.scvBytes(Buffer.from(sig)),
      ]),
    );

    return entry;
  }

  // --------------------------------------------------------------------------
  // Generic invoke: simulate → sign auth → re-simulate → submit
  // All Account contract operations delegate to this method.
  // --------------------------------------------------------------------------
  async invokeAccountFunction(
    functionName: string,
    args: xdr.ScVal[],
  ): Promise<TransactionResult> {
    if (!this._contractId || !this._deployer) {
      return { success: false, hash: "", error: "Not connected" };
    }

    try {
      const contractAddr = Address.fromString(this._contractId);
      const hostFunc = xdr.HostFunction.hostFunctionTypeInvokeContract(
        new xdr.InvokeContractArgs({
          contractAddress: contractAddr.toScAddress(),
          functionName,
          args,
        }),
      );

      // Deployer pays the fees; passkey signs the auth entry
      const feePayer = this._deployer;
      const account = await this.server.getAccount(feePayer.publicKey());

      // First simulation: discover auth entries
      const simTx = new TransactionBuilder(account, {
        fee: BASE_FEE,
        networkPassphrase: this.networkPassphrase,
      })
        .addOperation(Operation.invokeHostFunction({ func: hostFunc, auth: [] }))
        .setTimeout(60)
        .build();

      const simResult = await this.server.simulateTransaction(simTx);
      if (rpc.Api.isSimulationError(simResult)) {
        throw new Error(`Simulation failed: ${simResult.error}`);
      }

      const successSim = simResult as rpc.Api.SimulateTransactionSuccessResponse;
      const authEntries = successSim.result?.auth || [];
      const { sequence } = await this.server.getLatestLedger();
      const expirationLedger = sequence + 720;

      // Sign auth entries that belong to our contract address
      const signedAuth: xdr.SorobanAuthorizationEntry[] = [];
      for (const entry of authEntries) {
        const credSwitch = entry.credentials().switch().name;
        if (credSwitch === "sorobanCredentialsAddress") {
          const addr = Address.fromScAddress(entry.credentials().address().address());
          if (addr.toString() === this._contractId) {
            signedAuth.push(await this.signAuthEntry(entry, expirationLedger));
            continue;
          }
        }
        signedAuth.push(entry);
      }

      // Re-simulate with signed auth entries and submit.
      // No retry loop here — errors surface immediately to the UI so users
      // can see what happened and retry manually. Silent retries were masking
      // real errors and making the UI appear stuck.
      const account2 = await this.server.getAccount(feePayer.publicKey());
      const resimTx = new TransactionBuilder(account2, {
        fee: BASE_FEE,
        networkPassphrase: this.networkPassphrase,
      })
        .addOperation(Operation.invokeHostFunction({ func: hostFunc, auth: signedAuth }))
        .setTimeout(180)
        .build();

      const resimResult = await this.server.simulateTransaction(resimTx);
      if (rpc.Api.isSimulationError(resimResult)) {
        throw new Error(`Re-simulation failed: ${resimResult.error}`);
      }

      const prepared = rpc.assembleTransaction(
        resimTx,
        resimResult as rpc.Api.SimulateTransactionSuccessResponse,
      ).build();
      prepared.sign(feePayer);

      const sendResult = await this.server.sendTransaction(prepared);
      if (sendResult.status === ("ERROR" as rpc.Api.SendTransactionStatus)) {
        // Decode the error code for a human-readable message
        let errDetail = sendResult.errorResult?.toXDR("base64") ?? "unknown";
        try {
          const code = sendResult.errorResult?.result().switch().value;
          if (code === -5) errDetail = "txBAD_SEQ (stale sequence — please retry)";
          else if (code === -8) errDetail = "txNO_ACCOUNT (account not yet visible — please retry)";
        } catch { /* use raw base64 */ }
        throw new Error(`${functionName} send failed: ${errDetail}`);
      }

      const result = await this.server.pollTransaction(sendResult.hash, { attempts: POLL_ATTEMPTS });
      if (result.status !== rpc.Api.GetTransactionStatus.SUCCESS) {
        throw new Error(`${functionName} failed on-chain (status: ${String(result.status)})`);
      }

      return { success: true, hash: sendResult.hash };
    } catch (err) {
      return { success: false, hash: "", error: err instanceof Error ? err.message : "Unknown error" };
    }
  }

  // --------------------------------------------------------------------------
  // Invoke an external contract while signing Account auth entries with passkey.
  // Used to bypass the Hoops Router/Adapter for direct pool interactions.
  // --------------------------------------------------------------------------
  async invokeExternalContract(
    targetContract: string,
    functionName: string,
    args: xdr.ScVal[],
  ): Promise<TransactionResult> {
    if (!this._contractId || !this._deployer) {
      return { success: false, hash: "", error: "Not connected" };
    }

    try {
      const targetAddr = Address.fromString(targetContract);
      const hostFunc = xdr.HostFunction.hostFunctionTypeInvokeContract(
        new xdr.InvokeContractArgs({
          contractAddress: targetAddr.toScAddress(),
          functionName,
          args,
        }),
      );

      const feePayer = this._deployer;
      const account = await this.server.getAccount(feePayer.publicKey());

      // First simulation: discover auth entries
      const simTx = new TransactionBuilder(account, {
        fee: BASE_FEE,
        networkPassphrase: this.networkPassphrase,
      })
        .addOperation(Operation.invokeHostFunction({ func: hostFunc, auth: [] }))
        .setTimeout(60)
        .build();

      const simResult = await this.server.simulateTransaction(simTx);
      if (rpc.Api.isSimulationError(simResult)) {
        throw new Error(`Simulation failed: ${simResult.error}`);
      }

      const successSim = simResult as rpc.Api.SimulateTransactionSuccessResponse;
      const authEntries = successSim.result?.auth || [];
      const { sequence } = await this.server.getLatestLedger();
      const expirationLedger = sequence + 720;

      // Sign auth entries that belong to our Account contract (passkey)
      const signedAuth: xdr.SorobanAuthorizationEntry[] = [];
      for (const entry of authEntries) {
        const credSwitch = entry.credentials().switch().name;
        if (credSwitch === "sorobanCredentialsAddress") {
          const addr = Address.fromScAddress(entry.credentials().address().address());
          if (addr.toString() === this._contractId) {
            signedAuth.push(await this.signAuthEntry(entry, expirationLedger));
            continue;
          }
        }
        signedAuth.push(entry);
      }

      // Re-simulate with signed auth entries and submit
      const account2 = await this.server.getAccount(feePayer.publicKey());
      const resimTx = new TransactionBuilder(account2, {
        fee: BASE_FEE,
        networkPassphrase: this.networkPassphrase,
      })
        .addOperation(Operation.invokeHostFunction({ func: hostFunc, auth: signedAuth }))
        .setTimeout(180)
        .build();

      const resimResult = await this.server.simulateTransaction(resimTx);
      if (rpc.Api.isSimulationError(resimResult)) {
        throw new Error(`Re-simulation failed: ${resimResult.error}`);
      }

      const prepared = rpc.assembleTransaction(
        resimTx,
        resimResult as rpc.Api.SimulateTransactionSuccessResponse,
      ).build();
      prepared.sign(feePayer);

      const sendResult = await this.server.sendTransaction(prepared);
      if (sendResult.status === ("ERROR" as rpc.Api.SendTransactionStatus)) {
        let errDetail = sendResult.errorResult?.toXDR("base64") ?? "unknown";
        try {
          const code = sendResult.errorResult?.result().switch().value;
          if (code === -5) errDetail = "txBAD_SEQ (stale sequence \u2014 please retry)";
          else if (code === -8) errDetail = "txNO_ACCOUNT (account not yet visible \u2014 please retry)";
        } catch { /* use raw base64 */ }
        throw new Error(`${functionName} send failed: ${errDetail}`);
      }

      const result = await this.server.pollTransaction(sendResult.hash, { attempts: POLL_ATTEMPTS });
      if (result.status !== rpc.Api.GetTransactionStatus.SUCCESS) {
        throw new Error(`${functionName} failed on-chain (status: ${String(result.status)})`);
      }

      return { success: true, hash: sendResult.hash };
    } catch (err) {
      return { success: false, hash: "", error: err instanceof Error ? err.message : "Unknown error" };
    }
  }

  // --------------------------------------------------------------------------
  // Direct Aqua pool withdrawal (bypasses Hoops Router/Adapter).
  // Calls pool.withdraw(account, shares, [0, 0]) directly to avoid the
  // expensive LP token transfers that trigger Aqua checkpoint hooks and
  // exceed the Soroban budget when going through Router → Adapter → Pool.
  // --------------------------------------------------------------------------
  async redeemAqua(
    poolAddress: string,
    lpAmount: bigint,
  ): Promise<TransactionResult> {
    if (!this._contractId) {
      return { success: false, hash: "", error: "Not connected" };
    }

    return this.invokeExternalContract(poolAddress, "withdraw", [
      addrScVal(this._contractId),
      u128ScVal(lpAmount),
      xdr.ScVal.scvVec([u128ScVal(0n), u128ScVal(0n)]),
    ]);
  }

  // --------------------------------------------------------------------------
  // Direct Aqua pool claim (no passkey needed — pool.claim has no require_auth).
  // Deployer signs the TX; rewards are sent directly to the Account.
  // --------------------------------------------------------------------------
  async claimAqua(
    poolAddress: string,
  ): Promise<TransactionResult> {
    if (!this._contractId || !this._deployer) {
      return { success: false, hash: "", error: "Not connected" };
    }

    try {
      const pool = new Contract(poolAddress);
      const sourceAccount = await this.server.getAccount(this._deployer.publicKey());
      const tx = new TransactionBuilder(sourceAccount, {
        fee: BASE_FEE,
        networkPassphrase: this.networkPassphrase,
      })
        .addOperation(pool.call("claim", addrScVal(this._contractId)))
        .setTimeout(180)
        .build();

      const txHash = await simulateSignSend(
        this.server,
        tx,
        this._deployer,
        this.networkPassphrase,
      );
      return { success: true, hash: txHash };
    } catch (err) {
      return { success: false, hash: "", error: err instanceof Error ? err.message : "Claim failed" };
    }
  }

  /** Invoke an Account function using session key (no biometric prompt). */
  async invokeWithSession(
    functionName: string,
    args: xdr.ScVal[],
  ): Promise<TransactionResult> {
    if (!this._contractId || !this._deployer || !this._sessionKeypair) {
      return { success: false, hash: "", error: "No active session" };
    }

    try {
      const contractAddr = Address.fromString(this._contractId);
      const hostFunc = xdr.HostFunction.hostFunctionTypeInvokeContract(
        new xdr.InvokeContractArgs({
          contractAddress: contractAddr.toScAddress(),
          functionName,
          args,
        }),
      );

      const feePayer = this._deployer;
      const account = await this.server.getAccount(feePayer.publicKey());

      const simTx = new TransactionBuilder(account, {
        fee: BASE_FEE,
        networkPassphrase: this.networkPassphrase,
      })
        .addOperation(Operation.invokeHostFunction({ func: hostFunc, auth: [] }))
        .setTimeout(60)
        .build();

      const simResult = await this.server.simulateTransaction(simTx);
      if (rpc.Api.isSimulationError(simResult)) {
        throw new Error(`Simulation failed: ${simResult.error}`);
      }

      const successSim = simResult as rpc.Api.SimulateTransactionSuccessResponse;
      const authEntries = successSim.result?.auth || [];
      const { sequence } = await this.server.getLatestLedger();
      const expirationLedger = sequence + 720;

      // Sign auth entries with session key (no biometric)
      const signedAuth: xdr.SorobanAuthorizationEntry[] = [];
      for (const entry of authEntries) {
        const credSwitch = entry.credentials().switch().name;
        if (credSwitch === "sorobanCredentialsAddress") {
          const addr = Address.fromScAddress(entry.credentials().address().address());
          if (addr.toString() === this._contractId) {
            signedAuth.push(this.signAuthEntryWithSession(entry, expirationLedger));
            continue;
          }
        }
        signedAuth.push(entry);
      }

      const account2 = await this.server.getAccount(feePayer.publicKey());
      const resimTx = new TransactionBuilder(account2, {
        fee: BASE_FEE,
        networkPassphrase: this.networkPassphrase,
      })
        .addOperation(Operation.invokeHostFunction({ func: hostFunc, auth: signedAuth }))
        .setTimeout(180)
        .build();

      const resimResult = await this.server.simulateTransaction(resimTx);
      if (rpc.Api.isSimulationError(resimResult)) {
        throw new Error(`Re-simulation failed: ${resimResult.error}`);
      }

      const prepared = rpc.assembleTransaction(
        resimTx,
        resimResult as rpc.Api.SimulateTransactionSuccessResponse,
      ).build();
      prepared.sign(feePayer);

      const sendResult = await this.server.sendTransaction(prepared);
      if (sendResult.status === ("ERROR" as rpc.Api.SendTransactionStatus)) {
        let errDetail = sendResult.errorResult?.toXDR("base64") ?? "unknown";
        try {
          const code = sendResult.errorResult?.result().switch().value;
          if (code === -5) errDetail = "txBAD_SEQ (stale sequence — please retry)";
        } catch { /* use raw base64 */ }
        throw new Error(`${functionName} send failed: ${errDetail}`);
      }

      const result = await this.server.pollTransaction(sendResult.hash, { attempts: POLL_ATTEMPTS });
      if (result.status !== rpc.Api.GetTransactionStatus.SUCCESS) {
        throw new Error(`${functionName} failed on-chain (status: ${String(result.status)})`);
      }

      return { success: true, hash: sendResult.hash };
    } catch (err) {
      return { success: false, hash: "", error: err instanceof Error ? err.message : "Unknown error" };
    }
  }

  // --------------------------------------------------------------------------
  // Transfer tokens FROM the contract (calls Account::transfer)
  // --------------------------------------------------------------------------
  async transfer(
    tokenContract: string,
    to: string,
    amountStroops: bigint,
  ): Promise<TransactionResult> {
    return this.invokeAccountFunction("transfer", [
      addrScVal(tokenContract),
      addrScVal(to),
      i128ScVal(amountStroops),
    ]);
  }

  // --------------------------------------------------------------------------
  // Swap tokens via router (calls Account::swap)
  // --------------------------------------------------------------------------
  async swap(
    tokenIn: string,
    tokenOut: string,
    amount: bigint,
    bestHop: string,
    deadline: number,
  ): Promise<TransactionResult> {
    return this.invokeAccountFunction("swap", [
      addrScVal(tokenIn),
      addrScVal(tokenOut),
      i128ScVal(amount),
      addrScVal(bestHop),
      xdr.ScVal.scvU32(deadline),
    ]);
  }

  // --------------------------------------------------------------------------
  // Deposit into LP (calls Account::deposit)
  // --------------------------------------------------------------------------
  async deposit(
    usdc: string,
    amount: bigint,
    lpPlans: LpPlan[],
    deadline: number,
  ): Promise<TransactionResult> {
    const planScVals = lpPlans.map((p) => buildLpPlanScVal(p));
    return this.invokeAccountFunction("deposit", [
      addrScVal(usdc),
      i128ScVal(amount),
      xdr.ScVal.scvVec(planScVals),
      xdr.ScVal.scvU32(deadline),
    ]);
  }

  // --------------------------------------------------------------------------
  // Batch redeem: atomic multi-pool withdrawal (calls Account::batch_redeem)
  // --------------------------------------------------------------------------
  async batchRedeem(
    plans: RedeemPlan[],
    deadline: number,
  ): Promise<TransactionResult> {
    const planScVals = plans.map((p) => buildRedeemPlanScVal(p));
    return this.invokeAccountFunction("batch_redeem", [
      xdr.ScVal.scvVec(planScVals),
      xdr.ScVal.scvU32(deadline),
    ]);
  }

  // --------------------------------------------------------------------------
  // Soroswap-specific redeem: 2-step workaround for deployed adapter bug.
  // The deployed Soroswap adapter calls pair.withdraw() without first
  // transferring LP tokens to the pair, causing error #116.
  // Workaround: (1) transfer LP to pair, (2) call pair.withdraw(account).
  // --------------------------------------------------------------------------
  async redeemSoroswap(
    pairAddress: string,
    lpAmount: bigint,
  ): Promise<TransactionResult> {
    if (!this._contractId || !this._deployer) {
      return { success: false, hash: "", error: "Not connected" };
    }

    // Step 1: Transfer LP tokens from Account to the pair (passkey auth).
    // For Soroswap, the LP token IS the pair contract itself.
    const transferResult = await this.transfer(pairAddress, pairAddress, lpAmount);
    if (!transferResult.success) return transferResult;

    // Wait for the transfer TX to propagate into the simulation engine.
    // Without this, pair.withdraw() sees 0 LP tokens → Error #116.
    // Poll the Account's LP balance: once it drops below lpAmount, the transfer landed.
    for (let i = 0; i < 10; i++) {
      await new Promise(r => setTimeout(r, 2000));
      const remaining = await this.queryBalance(pairAddress);
      if (remaining < lpAmount) break;
    }

    // Step 2: Call pair.withdraw(account) — deployer signs, no passkey needed.
    // The pair burns LP tokens it holds and sends reserves to the account.
    try {
      const pairContract = new Contract(pairAddress);
      const sourceAccount = await this.server.getAccount(this._deployer.publicKey());
      const tx = new TransactionBuilder(sourceAccount, {
        fee: BASE_FEE,
        networkPassphrase: this.networkPassphrase,
      })
        .addOperation(pairContract.call("withdraw", new Address(this._contractId).toScVal()))
        .setTimeout(180)
        .build();

      const withdrawHash = await simulateSignSend(
        this.server,
        tx,
        this._deployer,
        this.networkPassphrase,
      );
      return { success: true, hash: withdrawHash };
    } catch (err) {
      return { success: false, hash: transferResult.hash, error: err instanceof Error ? err.message : "Withdraw step failed" };
    }
  }

  // --------------------------------------------------------------------------
  // Claim rewards (calls Account::claim)
  // --------------------------------------------------------------------------
  async claim(
    adapter: string,
    pool: string,
    rewardToken: string,
  ): Promise<TransactionResult> {
    return this.invokeAccountFunction("claim", [
      addrScVal(adapter),
      addrScVal(pool),
      addrScVal(rewardToken),
    ]);
  }

  // --------------------------------------------------------------------------
  // Session keys: one biometric → multiple TXs signed by temp ed25519 key
  // --------------------------------------------------------------------------

  /** Create a session key. Requires ONE passkey biometric prompt.
   *  After this, use invokeWithSession() for zero-biometric operations. */
  async createSession(
    scope: string[],
    maxAmount: bigint,
    ttlLedgers: number,
  ): Promise<TransactionResult> {
    // Generate ephemeral ed25519 keypair
    const sessionKeypair = Keypair.random();
    const pubkeyBytes = sessionKeypair.rawPublicKey();

    // Get current ledger to compute expiry
    const { sequence } = await this.server.getLatestLedger();
    const expiryLedger = sequence + ttlLedgers;

    // Build SessionKey struct (alphabetical field order for Soroban)
    const sessionKeyScVal = xdr.ScVal.scvMap([
      new xdr.ScMapEntry({
        key: xdr.ScVal.scvSymbol("expiry_ledger"),
        val: xdr.ScVal.scvU32(expiryLedger),
      }),
      new xdr.ScMapEntry({
        key: xdr.ScVal.scvSymbol("max_amount"),
        val: i128ScVal(maxAmount),
      }),
      new xdr.ScMapEntry({
        key: xdr.ScVal.scvSymbol("pubkey"),
        val: xdr.ScVal.scvBytes(Buffer.from(pubkeyBytes)),
      }),
      new xdr.ScMapEntry({
        key: xdr.ScVal.scvSymbol("scope"),
        val: xdr.ScVal.scvVec(scope.map(s => xdr.ScVal.scvSymbol(s))),
      }),
    ]);

    // This invocation triggers passkey auth (one biometric prompt)
    const result = await this.invokeAccountFunction("create_session", [sessionKeyScVal]);

    if (result.success) {
      this._sessionKeypair = sessionKeypair;
    }

    return result;
  }

  /** Check if a session key is active in memory. */
  get hasSession(): boolean {
    return this._sessionKeypair !== null;
  }

  /** Clear the in-memory session key. */
  clearSession(): void {
    this._sessionKeypair = null;
  }

  // --------------------------------------------------------------------------
  // Read-only queries (simulation only, no passkey auth needed)
  // --------------------------------------------------------------------------

  /** Query token balance for the smart account. */
  async queryBalance(tokenId: string): Promise<bigint> {
    if (!this._contractId) return 0n;
    try {
      // Simulation doesn't need a real funded account — use a dummy source.
      // This allows balance queries even without the deployer (cross-device).
      const sourceAccount = this._deployer
        ? await this.server.getAccount(this._deployer.publicKey())
        : new Account("GAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWHF", "0");
      const tokenContract = new Contract(tokenId);
      const tx = new TransactionBuilder(sourceAccount, {
        fee: BASE_FEE,
        networkPassphrase: this.networkPassphrase,
      })
        .addOperation(tokenContract.call("balance", new Address(this._contractId).toScVal()))
        .setTimeout(30)
        .build();
      const sim = await this.server.simulateTransaction(tx);
      if ("result" in sim && sim.result) {
        const retVal = (sim.result as { retval?: xdr.ScVal }).retval;
        if (retVal) return scValToI128(retVal);
      }
    } catch (err) {
      console.warn(`queryBalance(${tokenId}) failed:`, err);
    }
    return 0n;
  }

  // --------------------------------------------------------------------------
  // Get swap quotes from Router (simulation only — no signing needed)
  // --------------------------------------------------------------------------
  async getSwapQuotes(
    tokenIn: string,
    tokenOut: string,
    amount: bigint,
  ): Promise<SwapQuote[]> {
    try {
      const routerContract = new Contract(this.routerAddress);
      const sourceAccount = this._deployer
        ? await this.server.getAccount(this._deployer.publicKey())
        : new Account("GAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWHF", "0");

      const tx = new TransactionBuilder(sourceAccount, {
        fee: BASE_FEE,
        networkPassphrase: this.networkPassphrase,
      })
        .addOperation(routerContract.call(
          "get_all_quotes",
          i128ScVal(amount),
          addrScVal(tokenIn),
          addrScVal(tokenOut),
        ))
        .setTimeout(30)
        .build();

      const sim = await this.server.simulateTransaction(tx);
      if ("result" in sim && sim.result) {
        const retVal = (sim.result as { retval?: xdr.ScVal }).retval;
        if (retVal) {
          const parsed: unknown = scValToNative(retVal);
          return Array.isArray(parsed) ? parsed as SwapQuote[] : [];
        }
      }
    } catch (err) {
      console.warn("getSwapQuotes failed:", err);
    }
    return [];
  }

  /** Query Aqua LP share balance for the smart account.
   *  With V2 direct deposit, Account holds shares directly (no warehouse).
   *  Pass the pool's share_id (LP token contract) as shareTokenId. */
  async queryAquaLpBalance(shareTokenId: string): Promise<bigint> {
    return this.queryBalance(shareTokenId);
  }

  // --------------------------------------------------------------------------
  // Reset in-memory state (preserves localStorage for next login)
  // --------------------------------------------------------------------------
  resetInMemory(): void {
    this._contractId = null;
    this._credentialId = null;
    this._publicKey = null;
    this._deployer = null;
    this._sessionKeypair = null;
    this._transports = [];
  }

  // --------------------------------------------------------------------------
  // Disconnect (clears both in-memory AND localStorage)
  // --------------------------------------------------------------------------
  disconnect(): void {
    const cred = this._credentialId;
    this._contractId = null;
    this._credentialId = null;
    this._publicKey = null;
    this._deployer = null;
    this._sessionKeypair = null;
    if (typeof window !== "undefined") {
      // Remove scoped keys for the disconnecting credential
      if (cred) {
        localStorage.removeItem(scopedKey(cred, "contractId"));
        localStorage.removeItem(scopedKey(cred, "credentialId"));
        localStorage.removeItem(scopedKey(cred, "publicKey"));
        localStorage.removeItem(scopedKey(cred, "deployer"));
        localStorage.removeItem(scopedKey(cred, "transports"));
      }
      // Also remove any lingering legacy keys
      HoopsPasskeyKit.removeLegacyKeys();
    }
  }

  // --------------------------------------------------------------------------
  // Static cleanup helpers
  // --------------------------------------------------------------------------

  /** Remove all `hoops_pk_*` keys from localStorage (every user's data). */
  static clearAllWalletStorage(): void {
    if (typeof window === "undefined") return;
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(STORAGE_PREFIX)) toRemove.push(key);
    }
    for (const k of toRemove) localStorage.removeItem(k);
  }

  /** Remove only the legacy (pre-scoping) global keys. */
  static removeLegacyKeys(): void {
    if (typeof window === "undefined") return;
    for (const k of Object.values(LEGACY_KEYS)) localStorage.removeItem(k);
  }

  // --------------------------------------------------------------------------
  // Storage helpers
  // --------------------------------------------------------------------------
  private saveToStorage(): void {
    if (typeof window === "undefined") return;
    if (!this._credentialId) return;
    const cred = this._credentialId;
    if (this._contractId) localStorage.setItem(scopedKey(cred, "contractId"), this._contractId);
    localStorage.setItem(scopedKey(cred, "credentialId"), cred);
    if (this._publicKey) localStorage.setItem(scopedKey(cred, "publicKey"), Buffer.from(this._publicKey).toString("hex"));
    if (this._deployer) localStorage.setItem(scopedKey(cred, "deployer"), this._deployer.secret());
    if (this._transports.length > 0) localStorage.setItem(scopedKey(cred, "transports"), JSON.stringify(this._transports));
  }
}
